// BIGLI-11（PM Bridget 2026-08-21）：導覽清單的單一來源。
//
// 改版前這份清單有兩份各自硬編：Sidebar.js 的 NAV_GROUPS 與 DashboardSimple.js 的
// featuresSection。兩邊改一邊就會不同步（BIGLI-7 更名時就實際發生過）。現在側欄與
// 導覽總覽頁（pages/Home.js）都引用本檔，新增／改名只改這裡一處。
//
// 只放「導覽資料」，不放樣式與版型：側欄要 accordion、總覽頁要平鋪，兩邊各自渲染。
import {
  FaFileAlt,
  FaBox,
  FaAddressBook,
  FaUserCircle,
  FaUsers,
  FaBuilding,
} from 'react-icons/fa';

// BIGLI-3：導覽只保留三個主群組。營收管理（/invoices、原 /dashboard）僅移除導覽入口。
// BIGLI-44：上一行原本註明「/invoices 路由仍保留在 App.js，報價單→請款單的既有跳轉
// 不受影響」——該敘述已失效。PM 判定本專案不做請款模組，App.js 的 /invoices、
// /invoices/:id 兩條路由與報價單頁的所有請款跳轉都已下架，直接打網址會落到 NotFound。
// 請款模組要上線時，除了把路由與入口接回，這裡的導覽項目也要一併考慮是否加回來。
export const NAV_GROUPS = [
  {
    key: 'quotation',
    // BIGLI-113（PM 選方案 1）：LINE 新需求本質是外部送進來的詢價單，緊接著就轉報價單，
    // 原本獨立的 key:'line' 群組併進來、群組更名「報價/詢價管理」，首頁因此 4 卡滿一列。
    // 只改導覽群組 title，報價單列表頁內部的「報價管理」標題不動。
    label: '報價/詢價管理',
    icon: FaFileAlt,
    // 判斷群組 active 用的路徑前綴（含報價單詳情、編輯、出貨單、LINE 需求單）
    matches: ['/quotations', '/create', '/edit', '/shipping', '/line-inquiries'],
    items: [
      { to: '/create', label: '新增報價單' },
      { to: '/quotations', label: '報價單列表' },
      // BIGLI-59：LINE 官方帳號詢問進來的租借需求單
      // BIGLI-113（PM Bridget 2026-10-07「Line新需求放置第三排」）：從第一項移到最後
      { to: '/line-inquiries', label: 'LINE 新需求' },
    ],
  },
  {
    key: 'asset',
    // BIGLI-7 A：對外語彙統一為「攝影器材」，路由路徑 /assets、/schedule 不變。
    label: '攝影器材管理',
    icon: FaBox,
    matches: ['/assets', '/schedule'],
    items: [
      { to: '/assets', label: '攝影器材' },
      { to: '/schedule', label: '檔期資訊' },
    ],
  },
  {
    key: 'customer',
    label: '客戶管理',
    icon: FaAddressBook,
    matches: ['/customers'],
    items: [
      { to: '/customers/new', label: '新增客戶' },
      { to: '/customers', label: '客戶列表' },
    ],
  },
];

export const matchesGroup = (pathname, group) =>
  group.matches.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

export const findActiveGroupKey = (pathname) =>
  NAV_GROUPS.find((group) => matchesGroup(pathname, group))?.key || null;

// BIGLI-12（PM Bridget 2026-08-22 回覆 (a)）：設定類頁面只給「公司管理員與超級管理員」，
// 門市人員（role=manager）不揭露也進不去。這裡是唯一的揭露規則來源，
// 與 App.js 的 ProtectedRoute allowedRoles 對齊：
//   個人資料      — 所有登入者（含門市人員）
//   用戶管理      — admin / superadmin（superadmin 走 /users，admin 走自家公司底下）
//   公司基本資料  — admin / superadmin
//
// 一處與改版前不同，是為了驗收條件 #5「管理員可進入目前所有頁面」：
// 公司基本資料原本只揭露給 admin，superadmin（大力膠的管理員帳號就是 superadmin）
// 看不到入口，只能手打網址。
//
// BIGLI-21（PM 2026-08-25「角色僅需要有三種」）：清單內容不變，但語意更直白了——
// 收斂前 admin 是「公司管理員（含角色管理）」、旁邊還有一個進不來的舊 admin 變體；
// 收斂後 admin 就是唯一的「公司管理員」，這些頁＝公司管理員與超級管理員可進。
//
// BIGLI-34（PM Bridget 2026-08-31：「此系統不需要此區塊，請隱藏」）：
// /message-templates 這條路由整頁下架，這裡與導覽總覽頁的入口一併移除，
// 設定類只剩「個人資料／用戶管理／公司基本資料」。頁上還需要編輯的欄位
// （公司 LOGO、公司簽名、公司資訊、銀行資訊、AI 設定）已併入 /company-settings；
// MessageTemplates model 與 /api/message-templates 保留不動，只是沒有這個編輯入口了。
// 守門見 src/client/src/__tests__/messageTemplatesRetired.test.js。
export const SETTINGS_ROLES = ['admin', 'superadmin'];

export const canAccessSettings = (currentUser) =>
  !!currentUser && SETTINGS_ROLES.includes(currentUser.role);

export const buildAccountNavItems = (currentUser) => {
  if (!currentUser) return [];

  const isSuperAdmin = currentUser.role === 'superadmin';
  const isSettingsRole = canAccessSettings(currentUser);

  const items = [{ to: '/profile', label: '個人資料', icon: FaUserCircle }];

  if (isSettingsRole) {
    items.push({
      to: isSuperAdmin
        ? '/users'
        : `/companies/${currentUser.company?._id || currentUser.company}/users`,
      label: '用戶管理',
      icon: FaUsers,
    });
    items.push({ to: '/company-settings', label: '公司基本資料', icon: FaBuilding });
  }

  return items;
};
